import env from '@/config/env'
import { Link, Navigate, Route, Routes } from 'react-router'
import { HomePage } from './pages/home-page'
import { AuthLinkedInPage } from './pages/auth-page/auth-linkedin-page'
import { AuthGithubPage } from './pages/auth-page/auth-github-page'
import { AuthNotionPage } from './pages/auth-page/auth-notion-page'
import { AuthGoogleCalendarPage } from './pages/auth-page/auth-google-calendar-page'
import { AuthTikTokPage } from './pages/auth-page/auth-tiktok-page'
import { AuthInstagramPage } from './pages/auth-page/auth-instagram-page'
import { AuthFacebookPage } from './pages/auth-page/auth-facebook-page'
import { useUserContext } from './context/userLogin'
import { Layout } from './layout/Layout'
import { PricingPage } from './pages/pricing-page'
import { Cancel } from './pages/Cancel'
import { useEffect } from 'react'
import AuthPage from './pages/auth-page/auth-page'
import { AccountPage } from './pages/account-page/account-page'
import { AccountGeneralPage } from './pages/account-page/account-general-page'
import { AccountSocialMediaPage } from './pages/account-page/account-social-page'
import { AccountSubscriptionPage } from './pages/account-page/account-subscription-page'
import { AccountTermsPage } from './pages/account-page/account-terms-page'
import { AccountNotificationsPage } from './pages/account-page/account-notifications-page'
import { trpc } from './config/trpc'
import { toast } from 'sonner'
import { AuthUserPassVerifyPage } from './pages/auth-page/auth-userpass-verify-page'
import { AuthResetPasswordPage } from './pages/auth-page/auth-reset-password-page'
import { AdminPage } from './pages/admin-page/admin-page'
import { AdminLogsPage } from './pages/admin-page/admin-logs-page'
import { ScheduledPostsPage } from './pages/scheduled-page/scheduled-posts-page'
import { ScheduledPostPage } from './pages/scheduled-page/scheduled-post-page'
import { AutomationsPage } from './pages/automations-page'
import { MoodboardListPage } from './pages/moodboard-page/moodboard-list-page'
import { MoodboardDetailPage } from './pages/moodboard-page/moodboard-detail-page'
import { AccountApiPage } from './pages/account-page/account-api-page'
import { AccountMcpPage } from './pages/account-page/account-mcp-page'
import { OauthAuthorizePage } from './pages/oauth/authorize-page'
import { AuthGooglePage } from './pages/auth-page/auth-google-page'
import CampaignsListPage from './pages/campaign-page/campaigns-list-page'
import CampaignDetailPage from './pages/campaign-page/campaign-detail-page'
import { WorkspacePage } from './pages/workspace-page/workspace-page'
import BrandNewPage from './pages/brand-page/brand-new-page'
import BrandSettingsPage from './pages/brand-page/brand-settings-page'
import { SettingsPage } from './pages/settings-page/settings-page'
import { ContentConnectionsPage } from './pages/settings-page/content-connections-page'
import { CompanySetupPage, CompanySetupFinishPage } from './pages/company-setup-page'
import { PrivacyPage, TermsOfServicePage } from './pages/legal-page'
import { AnalyticsPage } from './pages/analytics-page/analytics-page'
import { AnalyticsPostDetailPage } from './pages/analytics-page/analytics-post-detail-page'
import { SharedViewPage } from './pages/shared-view-page'
import { AppsumoPage } from './pages/appsumo-page'
import { getAppsumoClaim } from './lib/appsumoClaim'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, isPending: isUserPending } = useUserContext()

  if (isUserPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // A verified AppSumo purchase is waiting to be linked — finish that first,
  // whatever page the login flow landed on.
  if (getAppsumoClaim()) {
    return <Navigate to="/appsumo" replace />
  }

  return children
}

const useVersionCheck = () => {
  const { data: serverVersion } = trpc.appVersion.useQuery(undefined, {
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: 'always',
  })

  useEffect(() => {
    // NOTE: cleaning up the version from local storage - remove after some time
    localStorage.removeItem('version')

    if (!serverVersion) return

    if (env.VITE_GIT_HASH !== serverVersion) {
      toast('A new version is available. Click here to update.', {
        duration: 10000,
        action: {
          label: 'Update',
          onClick: () => {
            window.location.reload()
          },
        },
      })
    }
  }, [serverVersion])
}

function App() {
  useVersionCheck()
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />

      <Route path="/auth/google" element={<AuthGooglePage />} />
      <Route path="/auth/linkedin" element={<AuthLinkedInPage />} />
      <Route path="/auth/instagram" element={<AuthInstagramPage />} />
      <Route path="/auth/facebook" element={<AuthFacebookPage />} />
      <Route path="/auth/tiktok" element={<AuthTikTokPage />} />
      <Route path="/auth/github" element={<AuthGithubPage />} />
      <Route path="/auth/notion" element={<AuthNotionPage />} />
      <Route path="/auth/google-calendar" element={<AuthGoogleCalendarPage />} />
      <Route path="/auth/userpass/verify" element={<AuthUserPassVerifyPage />} />
      <Route path="/auth/userpass/resetPassword" element={<AuthResetPasswordPage />} />

      {/* Public legal pages */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />

      {/* Public, token-scoped, read-only view of a brand's My Posts */}
      <Route path="/share/:token/my-posts" element={<SharedViewPage />} />

      {/* AppSumo lifetime-deal redemption (OAuth redirect target) */}
      <Route path="/appsumo" element={<AppsumoPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
        <Route index element={<HomePage />} />

        {/* Personas were replaced by moodboards; keep old links working. */}
        <Route path="/persona/*" element={<Navigate to="/moodboards" replace />} />

        <Route path="/moodboards">
          <Route index element={<MoodboardListPage />} />
          <Route path=":id" element={<MoodboardDetailPage />} />
        </Route>

        <Route path="/automations" element={<AutomationsPage />} />

        <Route path="/posts">
          <Route index element={<ScheduledPostsPage />} />
          <Route path=":id" element={<ScheduledPostPage />} />
        </Route>

        <Route path="/analytics">
          <Route index element={<AnalyticsPage />} />
          <Route path=":id" element={<AnalyticsPostDetailPage />} />
        </Route>

        <Route path="/campaign">
          <Route index element={<CampaignsListPage />} />
          <Route path=":id" element={<CampaignDetailPage />} />
        </Route>

        <Route path="/brand">
          <Route path="new" element={<BrandNewPage />} />
          {/* Brand settings moved under the brand-scoped /settings tab. */}
          <Route path="settings" element={<Navigate to="/settings" replace />} />
        </Route>

        {/* Brand-scoped settings: brand details + social connections. */}
        <Route path="/settings" element={<SettingsPage />}>
          <Route index element={<BrandSettingsPage />} />
          <Route path="social-media" element={<AccountSocialMediaPage />} />
          <Route path="connections" element={<ContentConnectionsPage />} />
        </Route>

        <Route path="/company-setup" element={<CompanySetupPage />} />
        <Route path="/company-setup/finish" element={<CompanySetupFinishPage />} />

        {/* Account pages */}
        <Route path="/account" element={<AccountPage />}>
          <Route index element={<AccountGeneralPage />} />
          <Route path="notifications" element={<AccountNotificationsPage />} />
          {/* Social media is brand-scoped; it now lives under /settings. Redirect
              the many existing links here instead of updating each call site. */}
          <Route path="social-media" element={<Navigate to="/settings/social-media" replace />} />
          <Route path="subscription" element={<AccountSubscriptionPage />} />
          <Route path="legal" element={<AccountTermsPage />} />
          <Route path="api" element={<AccountApiPage />} />
          <Route path="mcp" element={<AccountMcpPage />} />
        </Route>

        <Route path="/oauth/authorize" element={<OauthAuthorizePage />} />

        {/* Workspace pages */}
        <Route path="/workspace/:id" element={<WorkspacePage />} />

        {/* Pricing pages */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/cancel" element={<Cancel />} />

        {/* Admin pages */}
        <Route path="/admin">
          <Route index element={<AdminPage />} />
          <Route path="logs" element={<AdminLogsPage />} />
        </Route>
      </Route>

      <Route
        path="*"
        element={
          <>
            <p>404 not Found </p>
            <p>
              Back to
              <strong>
                <Link to="/">Home</Link>
              </strong>
            </p>
          </>
        }
      />
    </Routes>
  )
}

export default App
